/**
 * Parsing for the coding agent's `set_todos` tool. The call's `input` carries the list —
 * `{"todos":[{"id","text","status"}, ...]}`, with `evidence` on a completed item and `reason` on a
 * cancelled one — and is normalised to {@link TodoItem}s. Anything that doesn't match the shape
 * yields `null`, letting callers fall back rather than render a broken checklist.
 *
 * A list that parses is not necessarily the plan: the agent loop can refuse it (an item marked
 * completed after an edit nothing has checked since), which its result reports as an error.
 */

export type TodoStatus = 'completed' | 'in_progress' | 'pending' | 'cancelled';

export interface TodoItem {
  /** Stable across rewrites of `text`; empty when the payload had none. */
  id: string;
  status: TodoStatus;
  text: string;
  /** What justified a finished item — a completed one's evidence, a cancelled one's reason.
   *  Empty for pending and in-progress items. */
  note: string;
}

const TODO_STATUSES: readonly TodoStatus[] = ['completed', 'in_progress', 'pending', 'cancelled'];

function coerceValue(value: unknown): Record<string, unknown> | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    try {
      const parsed: unknown = JSON.parse(trimmed);
      return typeof parsed === 'object' && parsed !== null ? (parsed as Record<string, unknown>) : null;
    } catch {
      return null;
    }
  }
  if (typeof value === 'object') return value as Record<string, unknown>;
  return null;
}

function stringOf(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return '';
}

function noteFor(status: TodoStatus, entry: { evidence?: unknown; reason?: unknown }): string {
  if (status === 'completed') return stringOf(entry.evidence);
  if (status === 'cancelled') return stringOf(entry.reason);
  return '';
}

function toTodoItems(raw: Record<string, unknown>): TodoItem[] | null {
  const list = raw.todos;
  if (!Array.isArray(list)) return null;
  const items: TodoItem[] = [];
  for (const entry of list) {
    if (typeof entry !== 'object' || entry === null) continue;
    const fields = entry as { id?: unknown; status?: unknown; text?: unknown; evidence?: unknown; reason?: unknown };
    if (typeof fields.text !== 'string') continue;
    const status = TODO_STATUSES.includes(fields.status as TodoStatus) ? (fields.status as TodoStatus) : 'pending';
    items.push({ id: stringOf(fields.id), status, text: fields.text, note: noteFor(status, fields) });
  }
  return items;
}

/** Parse a `set_todos` call's `input`. */
export function parseTodos(input: unknown): TodoItem[] | null {
  const raw = coerceValue(input);
  return raw ? toTodoItems(raw) : null;
}

export interface TodoSummary {
  /** Items still in the plan: cancelled ones are out of it, so a finished plan reads "4 of 4". */
  total: number;
  completed: number;
  inProgress: number;
  pending: number;
  cancelled: number;
}

export function summarizeTodos(todos: TodoItem[]): TodoSummary {
  const count = (status: TodoStatus) => todos.filter((t) => t.status === status).length;
  const cancelled = count('cancelled');
  return {
    total: todos.length - cancelled,
    completed: count('completed'),
    inProgress: count('in_progress'),
    pending: count('pending'),
    cancelled,
  };
}
